import React from "react";
import styled from "styled-components";
import { AboutSection, ProjectSection, Layout, PdfText } from "../components";
import {
  Document,
  Page,
  View,
  StyleSheet,
  Link,
  Font,
} from "@react-pdf/renderer";
import { PDFViewer, PDFDownloadLink } from "@react-pdf/renderer";
import { experienceData } from "../data/experienceData";
import robotoRegular from "../assets/fonts/Roboto-Regular.ttf";
import robotoMedium from "../assets/fonts/Roboto-Medium.ttf";
import robotoBold from "../assets/fonts/Roboto-Bold.ttf";
import { getFontSize } from "../utils/getFontSize";
import { getGapMargin, lastMargin } from "../utils/getGapMargin";
import { getFileName, getDesiredRoleTitle, rolesKeys } from "../consts/roles";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
`;

const LinksContainer = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 100%;
  height: 50px;
`;

const SPDFDownloadLink = styled(PDFDownloadLink)`
  line-height: 1.6;
  color: hsl(349, 100%, 33%);
  border-bottom: 1px solid hsla(132, 6%, 15%, 0);
  font-size: 16px;

  &:hover {
    transform: scale(1.1);
    border-bottom: 1px solid hsla(132, 6%, 15%, 0.5);
    transition: 0.1s;

    @media screen and (max-width: 550px) {
      transform: scale(1.02);
    }
  }

  &:active {
    text-shadow: hsl(349, 100%, 33%) 0px 0px 1px;
  }
`;

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: robotoRegular,
      fontWeight: 400,
    },
    {
      src: robotoMedium,
      fontWeight: 500,
    },
    {
      src: robotoBold,
      fontWeight: 700,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 25,
  },
  expTitleContainer: {
    display: "flex",
    alignItems: "center",
    marginVertical: 10,
  },
  sectionTitle: {
    fontWeight: 500,
    letterSpacing: 3,
    fontSize: getFontSize(14),
    textTransform: "uppercase",
  },
  companyInfoContainer: {
    position: "relative",
    flexDirection: "row",
    alignItems: "stretch",
    justifyContent: "space-between",
    paddingVertical: 10,
    borderTop: "1px dashed hsl(349,100%,33%)",
  },
  companyPeriodContainer: {
    minWidth: 200,
  },
  companyNameContainer: {
    flexGrow: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  companyLinkText: {
    fontSize: getFontSize(15),
    fontWeight: 500,
    marginHorizontal: "5px",
  },
  companyDateContainer: {
    position: "absolute",
    right: 0,
    width: 200,
    alignItems: "center",
  },
  projectsTitleContainer: {
    alignItems: "center",
    marginBottom: 15,
  },
});

const SPdfViewer = styled(PDFViewer)`
  width: 100%;
  height: 100%;
`;

export const MyDocument = ({ desiredRole }) => {
  return (
    <Document
      title="Alexander_Piskun_cv"
      author="Alexander_Piskun_cv"
      subject="Alexander_Piskun_cv"
      creator="Alexander_Piskun_cv"
    >
      <Page size="A4" style={styles.page}>
        <View>
          <AboutSection desiredRole={desiredRole} />
          <View style={styles.expTitleContainer}>
            <PdfText style={styles.sectionTitle}>Experience</PdfText>
          </View>
          {experienceData.map((company, i, arr) => {
            const projects = [...company.projects].reverse();
            const isLast = i === arr.length - 1;
            const isOneProject = company.projects.length === 1;

            return (
              <View
                key={i}
                style={{ ...(isLast && { marginBottom: lastMargin }) }}
              >
                <View style={styles.companyInfoContainer}>
                  <View style={styles.companyPeriodContainer}>
                    <PdfText color="grey">{company.period}</PdfText>
                  </View>

                  <View style={styles.companyNameContainer}>
                    <PdfText color="grey">Company:</PdfText>
                    <Link src={company.companyLink}>
                      <PdfText style={styles.companyLinkText}>
                        {company.company}
                      </PdfText>
                    </Link>
                    <PdfText color="grey">- {company.companyType}</PdfText>
                  </View>

                  <PdfText color="grey">{company.duration}</PdfText>
                </View>
                <View>
                  {projects.map((project, i) => {
                    const isProduct = project.projectName === company.company;
                    const isLastProject = company.projects.length - 1 === i;

                    return (
                      <View
                        key={i}
                        style={{
                          marginBottom: getGapMargin(project.projectName),
                          ...(!isLastProject && {
                            borderBottom: "1px dashed grey",
                            marginBottom: 10,
                          }),
                        }}
                      >
                        <ProjectSection
                          isProduct={isProduct}
                          isOneProject={isOneProject}
                          {...project}
                        />
                      </View>
                    );
                  })}
                </View>
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

const prodDesiredRole = rolesKeys.frontEnd;

export const Experience = () => {
  return (
    <Layout>
      <Container>
        <LinksContainer>
          {Object.keys(rolesKeys).map((roleKey) => {
            const shortTechTitle = getFileName(roleKey);

            return (
              <SPDFDownloadLink
                key={roleKey}
                document={<MyDocument desiredRole={roleKey} />}
                fileName={shortTechTitle}
              >
                {({ loading }) =>
                  loading
                    ? "Loading document..."
                    : `Download ${getDesiredRoleTitle(roleKey)} cv!`
                }
              </SPDFDownloadLink>
            );
          })}
        </LinksContainer>
        <SPdfViewer showToolbar={false}>
          <MyDocument desiredRole={prodDesiredRole} />
        </SPdfViewer>
      </Container>
    </Layout>
  );
};
